import { Drawer } from '@mui/material';
import { AnimatePresence } from 'motion/react';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { getCart } from '@/clients/storefront/storefrontClient';
import { isStaleCart } from '@/clients/storefront/storefrontService';
import useSunrunShopStore, {
  useSunrunShopActions,
} from '@/data-store/zustand/hooks/useSunrunShopStore';
import useStyles from './Cart.styles';
import EmptyCart from './EmptyCart';
import FilledCart from './FilledCart';

const Cart: React.FC = () => {
  const { cart, isCartOpen, storefrontCartEnv } = useSunrunShopStore(
    'cart',
    'isCartOpen',
    'storefrontCartEnv'
  );
  const { setIsCartOpen, setCart, setCartId } = useSunrunShopActions();
  const { classes } = useStyles();
  const router = useRouter();

  const handleClose = () => setIsCartOpen(false);
  const isEmpty = !cart?.lines.edges.length;

  useEffect(() => {
    setIsCartOpen(false);
  }, [setIsCartOpen]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to fire on route change
  useEffect(() => {
    const fetchCart = async () => {
      if (!cart?.id) return;

      const currentCart = await getCart(cart.id, storefrontCartEnv);
      if (currentCart) {
        setCart(currentCart);
      } else {
        setCartId(null);
      }
    };

    fetchCart();
  }, [cart?.id, router.asPath, setCartId, setCart]);

  useEffect(() => {
    if (isCartOpen && cart?.id) {
      getCart(cart.id, storefrontCartEnv).then((updatedCart) => {
        if (updatedCart) {
          setCart(updatedCart);
        }
      });
    }
  }, [isCartOpen, cart?.id, storefrontCartEnv, setCart]);

  useEffect(() => {
    if (cart?.updatedAt && isStaleCart(cart.updatedAt as string)) {
      setCart(null);
    }
  }, [cart, setCart]);

  return (
    <Drawer
      anchor='right'
      keepMounted
      open={isCartOpen}
      onClose={handleClose}
      PaperProps={{ className: classes.paper }}
      slotProps={{
        backdrop: { className: classes.backdrop },
        root: { className: classes.root },
      }}
    >
      <AnimatePresence mode='wait' initial={false}>
        {isEmpty ? <EmptyCart key='empty' /> : <FilledCart key='filled' />}
      </AnimatePresence>
    </Drawer>
  );
};

export default Cart;
